/**
 * Pricing Plans Configuration
 * 
 * Centralized configuration for all subscription tiers.
 * This is the single source of truth for pricing, features, and Stripe IDs.
 * 
 * IMPROVEMENT over Svelte version:
 * - Single file for all pricing config (was split across multiple files)
 * - Strongly typed with feature flags
 * - Clearer organization of payment links vs price IDs
 */

import type { PricingPlan, FeatureAccess, PlanTier } from '@/lib/types/billing'

// ============================================================================
// Default Feature Access by Plan
// ============================================================================

const FREE_FEATURES: FeatureAccess = {
  analytics: true,
  realTimeAnalytics: false,
  splitTesting: false,
  activeSplitTestLimit: 0,
  heatmaps: false,
  heatmapLimit: 0,
  funnels: false,
  funnelLimit: 0,
  aiCopilot: false,
  crossDomainTracking: false,
  teamAccess: false,
  teamSeats: 0,
  whiteLabel: false,
  apiAccess: false,
  slackSupport: false,
  revenueAttribution: false,
  revenueLimit: 0,
}

/** Plus Legacy - legacy plan without AI Insights */
const PLUS_LEGACY_FEATURES: FeatureAccess = {
  analytics: true,
  realTimeAnalytics: true,
  splitTesting: true,
  activeSplitTestLimit: 1,
  heatmaps: false,
  heatmapLimit: 0,
  funnels: false,
  funnelLimit: 0,
  aiCopilot: false, // AI Insights not available on Plus Legacy
  crossDomainTracking: false,
  teamAccess: false,
  teamSeats: 0,
  whiteLabel: false,
  apiAccess: true,
  slackSupport: false,
  revenueAttribution: false,
  revenueLimit: 0,
};

const PLUS_FEATURES: FeatureAccess = {
  analytics: true,
  realTimeAnalytics: true,
  splitTesting: true,
  activeSplitTestLimit: 1,
  heatmaps: true,
  heatmapLimit: 1,
  funnels: true,
  funnelLimit: 1,
  aiCopilot: true, // 25 AI Insights/month
  crossDomainTracking: true,
  teamAccess: false,
  teamSeats: 0,
  whiteLabel: false,
  apiAccess: true,
  slackSupport: false,
  revenueAttribution: true,
  revenueLimit: 0,
}

const BUSINESS_FEATURES: FeatureAccess = {
  analytics: true,
  realTimeAnalytics: true,
  splitTesting: true,
  activeSplitTestLimit: 5,
  heatmaps: true,
  heatmapLimit: 5,
  funnels: true,
  funnelLimit: 5,
  aiCopilot: true,
  crossDomainTracking: true,
  teamAccess: false,
  teamSeats: 0,
  whiteLabel: false,
  apiAccess: true,
  slackSupport: false,
  revenueAttribution: true,
  revenueLimit: 10000, // $10K/mo
}

const SCALE_FEATURES: FeatureAccess = {
  analytics: true,
  realTimeAnalytics: true,
  splitTesting: true,
  activeSplitTestLimit: Infinity,
  heatmaps: true,
  heatmapLimit: Infinity,
  funnels: true,
  funnelLimit: Infinity,
  aiCopilot: true,
  crossDomainTracking: true,
  teamAccess: true,
  teamSeats: 5,
  whiteLabel: true,
  apiAccess: true,
  slackSupport: true,
  revenueAttribution: true,
  revenueLimit: 100000, // $100K/mo
}

// ============================================================================
// Pricing Plans
// ============================================================================

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'plus',
    name: 'Plus',
    description: 'Perfect for freelancers, portfolio sites, and single landing pages testing basic ideas.',
    
    monthlyPrice: 19,
    annualPrice: 19,
    annualTotal: 228,
    annualSavings: 0,
    
    priceDisplay: {
      monthly: '$19/month',
      annual: '$19/month',
    },
    annualChargeDisplay: 'Billed annually: $228',
    annualChargeAmount: '$228',
    
    stripePriceIds: {
      monthly: 'price_plus_monthly',
      annual: 'price_plus_annual',
    },
    
    // Free trial payment links
    trialPaymentLinks: {
      annual: 'https://buy.stripe.com/fZebJ55X883xfN614j',
      monthly: 'https://buy.stripe.com/00gaF1clw4Rl30k28h',
    },
    
    // Direct payment links (no trial)
    directPaymentLinks: {
      annual: 'https://buy.stripe.com/3csdRddpAdnRfN6fZg',
      monthly: 'https://buy.stripe.com/dR614r4T4cjNasM28a',
    },
    
    eventLimit: 10000,
    websiteLimit: 5,
    
    features: [
      '10K event datapoints',
      '5 websites',
      '1 A/B split test',
      '1 funnel',
      '1 heatmap',
      'Revenue tracking',
      'Cross-domain tracking',
      '25 AI insights a month',
      'Meta and Google Ads attribution',
      'API access',
    ],

    featureFlags: PLUS_FEATURES,
  },
  
  {
    id: 'business',
    name: 'Business',
    description: 'Ideal for growing SaaS companies, e-commerce stores, and marketing teams managing multiple campaigns.',
    badge: 'Most popular',
    isPopular: true,
    
    monthlyPrice: 79,
    annualPrice: 65,
    annualTotal: 780,
    annualSavings: 168,
    
    priceDisplay: {
      monthly: '$79/month',
      annual: '$65/month',
    },
    annualChargeDisplay: 'Billed annually: $780',
    annualChargeAmount: '$780',
    
    stripePriceIds: {
      monthly: 'price_business_monthly',
      annual: 'price_business_annual',
    },
    
    trialPaymentLinks: {
      annual: 'https://buy.stripe.com/aEU4gD99k3NheJ2aES',
      monthly: 'https://buy.stripe.com/00g5kHbhs97B6cwbIP',
    },
    
    directPaymentLinks: {
      annual: 'https://buy.stripe.com/bIY9AXgBM6Zt0Sc6oF',
      monthly: 'https://buy.stripe.com/eVa3czclwcjN7gA7st',
    },
    
    eventLimit: 500000,
    websiteLimit: Infinity,
    
    // Sentence case, as the upgrade modal and plan picker print them (Paper
    // "03 - App · Paywall & billing").
    features: [
      '500K event datapoints',
      'Unlimited websites',
      '5 A/B split tests',
      '5 funnels',
      '5 heatmaps',
      'Revenue tracking',
      'Cross-domain tracking',
      '200 AI insights a month',
      'All ad platform connectors',
      'API access',
    ],

    featureFlags: BUSINESS_FEATURES,
  },
  
  {
    id: 'scale',
    name: 'Scale',
    description: 'Built for agencies, high-traffic sites, and revenue-focused organizations that need full attribution and collaboration.',
    
    monthlyPrice: 279,
    annualPrice: 239,
    annualTotal: 2868,
    annualSavings: 480,
    
    priceDisplay: {
      monthly: '$279/month',
      annual: '$239/month',
    },
    annualChargeDisplay: 'Billed annually: $2,868',
    annualChargeAmount: '$2,868',
    monthlyChargeAmount: '$279',
    
    stripePriceIds: {
      monthly: 'price_scale_monthly',
      annual: 'price_scale_annual',
    },
    
    trialPaymentLinks: {
      annual: 'https://buy.stripe.com/6oEcN90CO5VpdEYdR3',
      monthly: 'https://buy.stripe.com/bIY28v71c3NhgRa7sx',
    },
    
    directPaymentLinks: {
      annual: 'https://buy.stripe.com/bIY6oL2KWabF7gAeVa',
      monthly: 'https://buy.stripe.com/7sIbJ55X8fvZ1WgfZ1',
    },
    
    eventLimit: 1000000,
    websiteLimit: Infinity,
    
    features: [
      '1M event datapoints',
      'Unlimited websites',
      'Unlimited A/B split tests',
      'Unlimited funnels',
      'Unlimited heatmaps',
      'Revenue tracking',
      'Cross-domain tracking',
      '5 team members',
      '1,000 AI insights a month',
      'All ad platform connectors',
      'Priority AI processing',
      'API access',
    ],

    featureFlags: SCALE_FEATURES,
  },
]

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get a plan by its tier ID
 */
export function getPlanById(id: PlanTier): PricingPlan | undefined {
  return PRICING_PLANS.find(plan => plan.id === id)
}

/**
 * Get the appropriate payment link for a plan
 */
export function getPaymentLink(
  plan: PricingPlan,
  isAnnual: boolean,
  usedTrial: boolean,
  prefillEmail?: string,
  userId?: string
): string {
  const links = usedTrial ? plan.directPaymentLinks : plan.trialPaymentLinks
  const baseUrl = isAnnual ? links.annual : links.monthly
  
  const params = new URLSearchParams()
  if (prefillEmail) {
    params.set('prefilled_email', prefillEmail)
  }
  if (userId) {
    params.set('client_reference_id', userId)
  }
  
  const queryString = params.toString()
  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

/**
 * Free start and Agents (backend `FREE_START` / `AGENTS`): every capability
 * through the API and MCP, no feature caps; data points are the only meter.
 * The web app is account management only on these plans, so dashboard-only
 * features (AI Copilot, team, white label) stay off.
 */
export const AGENT_PLAN_FEATURES: FeatureAccess = {
  analytics: true,
  realTimeAnalytics: true,
  splitTesting: true,
  activeSplitTestLimit: Infinity,
  heatmaps: true,
  heatmapLimit: Infinity,
  funnels: true,
  funnelLimit: Infinity,
  aiCopilot: false,
  crossDomainTracking: true,
  teamAccess: false,
  teamSeats: 1,
  whiteLabel: false,
  apiAccess: true,
  slackSupport: false,
  revenueAttribution: true,
  revenueLimit: Infinity,
}

const AGENT_PLAN_IDS = new Set(['FREE_START', 'AGENTS'])

/** True for the backend's prepaid agent plans, from `/billing/status`. */
export function isAgentPlanId(planId: string | null | undefined, planKind?: string | null): boolean {
  return (
    AGENT_PLAN_IDS.has(String(planId || '').toUpperCase()) ||
    AGENT_PLAN_IDS.has(String(planKind || '').toUpperCase())
  )
}

/**
 * Get default feature access for a plan tier
 */
export function getFeatureAccessForPlan(tier: PlanTier): FeatureAccess {
  switch (tier) {
    case 'plus':
      return PLUS_FEATURES
    case 'plus_legacy':
      return PLUS_LEGACY_FEATURES
    case 'business':
      return BUSINESS_FEATURES
    case 'scale':
      return SCALE_FEATURES
    default:
      return FREE_FEATURES
  }
}

/** Canonical UI names for plan tiers — Plus Legacy is named, never "your plan". */
const PLAN_DISPLAY_NAMES: Record<PlanTier, string> = {
  free: 'Free',
  plus: 'Plus',
  plus_legacy: 'Plus (Legacy)',
  business: 'Business',
  scale: 'Scale',
}

/**
 * Human-readable plan name for limit copy and badges.
 * Plus Legacy always resolves to "Plus (Legacy)" even when the API label
 * is a variant like "Plus Legacy". Otherwise prefers a non-empty `planName`.
 */
export function getPlanDisplayName(
  tier?: string | null,
  planName?: string | null,
): string {
  const t = (tier ?? '').toLowerCase()
  const name = (planName ?? '').trim()
  const lowerName = name.toLowerCase()

  if (t === 'plus_legacy' || (lowerName.includes('plus') && lowerName.includes('legacy'))) {
    return PLAN_DISPLAY_NAMES.plus_legacy
  }
  if (name && name !== 'Free') return name
  if (t in PLAN_DISPLAY_NAMES) return PLAN_DISPLAY_NAMES[t as PlanTier]
  return PLAN_DISPLAY_NAMES.free
}

/**
 * Calculate days left in trial
 */
export function calculateTrialDaysLeft(trialEndsAt: Date | string | null): number {
  if (!trialEndsAt) return 0
  
  const endDate = typeof trialEndsAt === 'string' ? new Date(trialEndsAt) : trialEndsAt
  const now = new Date()
  const diffMs = endDate.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  
  return Math.max(0, diffDays)
}

/**
 * Get urgency level based on trial days left
 */
export function getTrialUrgencyLevel(daysLeft: number): 'normal' | 'high' | 'critical' | 'expired' {
  if (daysLeft <= 0) return 'expired'
  if (daysLeft <= 1) return 'critical'
  if (daysLeft <= 3) return 'high'
  return 'normal'
}

/**
 * Get urgency message based on days left
 * @deprecated Use getTrialPhaseCopy() instead — see Paper spec §03.
 */
function getTrialUrgencyMessage(daysLeft: number, planName: string = 'Business'): string {
  if (daysLeft <= 0) {
    return "Don't Lose Your Data & Insights"
  }
  if (daysLeft === 1) {
    return "Final 24 hours—Your data stops collecting tomorrow"
  }
  if (daysLeft <= 3) {
    return `Only ${daysLeft} days left to lock in your ${planName} plan`
  }
  return `${daysLeft} days left in your trial`
}

// ============================================================================
// Trial Phase Engine (Paper spec §03 — five-voice escalation)
// ============================================================================

export type TrialPhase =
  | 'just_started' // Day 0, forest, celebration
  | 'ambient'      // Day 1-3, slate, informational
  | 'warming'      // Day 4-5, amber, first data mention
  | 'final_24h'    // Last 24h, rust, price on button
  | 'expired'      // post-expiry, ink, loss-aversion

export interface TrialUsageStats {
  sessions_n: number | null
  heatmaps_n: number | null
  funnels_n: number | null
}

interface TrialPhaseCopy {
  eyebrow?: string
  message: string
  ctaText: string
  /** When true, CTA bypasses pricing modal and goes direct to Stripe checkout. */
  ctaSkipModal: boolean
  /** When true, banner cannot be dismissed (rust/ink). */
  blocking: boolean
  /** Dismissal reshow window in hours (0 = non-dismissible). */
  dismissReshowHours: number
}

/**
 * Calculate hours left in trial (precision needed for final_24h phase countdown).
 */
export function calculateTrialHoursLeft(trialEndsAt: Date | string | null): number {
  if (!trialEndsAt) return 0
  const endDate = typeof trialEndsAt === 'string' ? new Date(trialEndsAt) : trialEndsAt
  const t = endDate.getTime()
  if (!Number.isFinite(t)) return 0
  return Math.max(0, Math.floor((t - Date.now()) / (1000 * 60 * 60)))
}

/**
 * Map trial state → one of five phases.
 *
 * Why hours-based final_24h: the rust phase needs precision the day-based
 * model loses (e.g. "2 hours left" still reads as Day 1 = 1 day left).
 *
 * Precedence: expired > just_started > final_24h > warming > ambient.
 * just_started intentionally overrides final_24h — a user who first
 * activates their trial in the final 24h still deserves the celebration
 * surface before the urgency surface kicks in on their next session.
 */
export function getTrialPhase(
  daysLeft: number,
  hoursLeft: number,
  justActivatedThisSession: boolean
): TrialPhase {
  if (hoursLeft <= 0 && daysLeft <= 0) return 'expired'
  if (justActivatedThisSession) return 'just_started'
  if (hoursLeft <= 24) return 'final_24h'
  if (daysLeft <= 3) return 'warming'
  return 'ambient'
}

/**
 * Format a date as "Sep 28", the form every billing banner and card uses.
 * Returns null for a missing or unparseable date so callers can drop the clause.
 */
export function formatShortDate(date: Date | string | null | undefined): string | null {
  if (!date) return null
  const d = typeof date === 'string' ? new Date(date) : date
  if (!Number.isFinite(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Pluralize helper for data-injected copy (handles 1 vs many).
 */
function pluralize(n: number, singular: string, plural?: string): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? singular : (plural ?? `${singular}s`)}`
}

/** "7,280 sessions and 4 funnels", or null when there is nothing to name. */
function joinKept(stats: TrialUsageStats, sessionNoun: string): { text: string; plural: boolean } | null {
  const counts: number[] = []
  const parts: string[] = []
  if (stats.sessions_n != null && stats.sessions_n > 0) {
    parts.push(pluralize(stats.sessions_n, sessionNoun))
    counts.push(stats.sessions_n)
  }
  if (stats.funnels_n != null && stats.funnels_n > 0) {
    parts.push(pluralize(stats.funnels_n, 'funnel'))
    counts.push(stats.funnels_n)
  }
  if (parts.length === 0) return null
  return { text: parts.join(' and '), plural: parts.length > 1 || counts[0] !== 1 }
}

/** Monthly data-point allowance as the UI prints it ("1M", "10K"). */
export function formatDataPointAllowance(limit: number): string {
  if (!Number.isFinite(limit) || limit <= 0) return 'unlimited'
  if (limit >= 1_000_000) return `${+(limit / 1_000_000).toFixed(1)}M`
  if (limit >= 1_000) return `${+(limit / 1_000).toFixed(1)}K`
  return limit.toLocaleString('en-US')
}

/**
 * Return the copy + CTA shape for the given phase (Paper "03 - App · Paywall &
 * billing", Home · Trial banners and Trial expired paywall).
 * Stats degrade gracefully — null fields drop their clauses from the message.
 */
export function getTrialPhaseCopy(
  phase: TrialPhase,
  stats: TrialUsageStats,
  options: {
    planName: string
    trialEndsAt: Date | string | null
    monthlyPrice: number
    daysLeft: number
    hoursLeft: number
  }
): TrialPhaseCopy {
  const { planName, trialEndsAt, monthlyPrice, daysLeft, hoursLeft } = options
  const endDate = formatShortDate(trialEndsAt)
  const plan = getPlanById(planName.toLowerCase().includes('scale') ? 'scale' : 'business')
  const eventCap = formatDataPointAllowance(Number(plan?.eventLimit ?? 0))
  const daysLabel = pluralize(daysLeft, 'day')

  switch (phase) {
    case 'just_started':
      return {
        eyebrow: 'Trial unlocked',
        message: `${planName} trial unlocked. 14 days free. Heatmaps, funnels and AI Copilot are yours${endDate ? ` until ${endDate}` : ''}.`,
        ctaText: 'Show me around',
        ctaSkipModal: false,
        blocking: false,
        dismissReshowHours: 0, // self-fades, won't reshow
      }

    case 'ambient':
      return {
        eyebrow: `${daysLabel} left`,
        message: `You're on the ${planName} trial. Track up to ${eventCap} data points and run heatmaps and funnels${endDate ? ` through ${endDate}` : ''}.`,
        ctaText: 'View plans',
        ctaSkipModal: false,
        blocking: false,
        dismissReshowHours: 24,
      }

    case 'warming': {
      // Inject real data when we have it; otherwise stick with the generic line.
      const clauses: string[] = []
      if (stats.sessions_n != null && stats.sessions_n > 0) clauses.push(`captured ${pluralize(stats.sessions_n, 'session')}`)
      if (stats.funnels_n != null && stats.funnels_n > 0) clauses.push(`built ${pluralize(stats.funnels_n, 'funnel')}`)
      const dataLine = clauses.length > 0 ? ` You've ${clauses.join(' and ')}.` : ''
      const lockLine = endDate ? ` Lock it in before ${endDate}.` : ' Lock it in before it ends.'

      return {
        eyebrow: `${daysLabel} left`,
        message: `${daysLabel} left on your ${planName} trial.${dataLine}${lockLine}`,
        ctaText: 'Lock in your plan',
        ctaSkipModal: false,
        blocking: false,
        dismissReshowHours: 12,
      }
    }

    case 'final_24h': {
      const kept = joinKept(stats, 'session')
      const keepLine = kept
        ? `You keep ${kept.text}, but new visits stop counting.`
        : 'Your data stays, but new visits stop counting.'

      return {
        eyebrow: hoursLeft > 0 ? `Final ${pluralize(hoursLeft, 'hour')}` : 'Final hours',
        message: `Your trial ends tonight and tracking pauses tomorrow. ${keepLine}`,
        ctaText: `Subscribe now · $${monthlyPrice}/mo`,
        ctaSkipModal: true, // direct to Stripe — earned the price-on-button
        blocking: true,
        dismissReshowHours: 0,
      }
    }

    case 'expired': {
      // No deletion date: collected data is kept, so the copy never threatens it.
      const kept = joinKept(stats, 'recorded session')
      const stopped = endDate ? `Tracking stopped on ${endDate}.` : 'Tracking has stopped.'
      const savedLine = kept
        ? `Your ${kept.text} ${kept.plural ? 'are' : 'is'} saved.`
        : 'Everything you collected is saved.'

      return {
        eyebrow: 'Tracking paused',
        message: `${stopped} ${savedLine} Reactivate to pick up where you left off.`,
        ctaText: `Reactivate now · $${monthlyPrice}/mo`,
        ctaSkipModal: true,
        blocking: true,
        dismissReshowHours: 0,
      }
    }
  }
}

// ============================================================================
// Trial Duration Constants
// ============================================================================

const TRIAL_DURATION_DAYS = 14
export const DISMISSAL_DURATION_MINUTES = 15 // How long to hide after dismiss
export const LIMIT_WARNING_THRESHOLD = 0.75 // Show warning at 75% usage

// ============================================================================
// localStorage Keys
// ============================================================================

export const STORAGE_KEYS = {
  TRIAL_DIALOG_DISMISSED: 'humblytics_trial_dialog_dismissed',
  LIMIT_BANNER_DISMISSED: 'humblytics_limit_banner_dismissed',
  TRIAL_WIDGET_VISIBLE: 'humblytics_trial_widget_visible',
} as const

